import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent, CART_RESTORE_SYNC_SOURCE } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  // snippets/cart-bubble.liquid renders the count as "(n)", so read and write that same
  // format — parseInt("(2)") is NaN, which blanked the bubble / stored "NaN" in sessionStorage.

  /** @type {number} */
  get currentCartCount() {
    return parseInt((this.refs.cartBubbleCount.textContent ?? '').replace(/\D/g, '') || '0', 10);
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent = value < 100 ? `(${value})` : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';
    // A silent re-sync after back/forward navigation shouldn't play the "item added" bounce
    const animate = event.detail.data?.source !== CART_RESTORE_SYNC_SOURCE;

    this.renderCartBubble(itemCount, comingFromProductForm, animate);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);

  // Pages restored via the browser back/forward buttons show the cart as it was when first
  // rendered, e.g. after a full-page form POST to /cart. Re-sync once per restore by
  // broadcasting the live cart, which updates every cart icon and makes the cart drawer
  // re-render itself. Registered inside the define guard so it runs once per page, however
  // many <cart-icon> instances there are.
  const syncCartAfterHistoryRestore = async () => {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, { cache: 'no-store' });
      const cart = await response.json();

      // Other listeners (e.g. cart items) live in modules that may not have run yet, and all
      // deferred modules have executed by DOMContentLoaded
      if (document.readyState === 'loading') {
        await new Promise((resolve) => document.addEventListener('DOMContentLoaded', resolve, { once: true }));
      }

      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cart-icon', {
          itemCount: cart.item_count,
          source: CART_RESTORE_SYNC_SOURCE,
        })
      );
    } catch (_) {
      // no-op: the page keeps its existing cart state
    }
  };

  // bfcache restore: the page resumes as-is and pageshow fires straight away
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncCartAfterHistoryRestore();
  });

  // Non-bfcache restore: the page reloads from the history cache. Sync now instead of on
  // pageshow, which only fires after the load event (every image and third-party script).
  const [navigation] = /** @type {PerformanceNavigationTiming[]} */ (performance.getEntriesByType('navigation'));
  if (navigation?.type === 'back_forward') syncCartAfterHistoryRestore();
}
